import React from 'react';
import { cn } from '@/utils/cn';

export interface TeeToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  hint?: string;
  className?: string;
  ariaLabel?: string;
  hintId?: string;
}

export const TeeToggle = React.forwardRef<HTMLButtonElement, TeeToggleProps>(
  (
    {
      checked = false,
      onChange,
      disabled = false,
      label,
      hint,
      className,
      ariaLabel,
      hintId,
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled) {
        onChange?.(!checked);
      }
    };

    const labelClasses = cn(
      'font-[family-name:var(--type-font-family-body,"Urbanist:medium",sans-serif)]',
      'font-medium',
      'text-body-s',
      'text-[var(--text-label-title)]'
    );

    const hintClasses = cn(
      'font-[family-name:var(--type-font-family-body,"Urbanist:medium",sans-serif)]',
      'font-medium',
      'text-body-xs',
      'text-[var(--text-label-sub-label)]'
    );

    return (
      <div className="flex w-full flex-col gap-1.5">
        {label && <label className={labelClasses}>{label}</label>}

        <button
          ref={ref}
          onClick={handleClick}
          disabled={disabled}
          aria-label={ariaLabel ?? label}
          aria-describedby={hint ? hintId : undefined}
          className={cn(
            'relative inline-flex items-center rounded-full p-[2px] transition-colors duration-200',
            'w-[44px] h-[24px]',
            checked
              ? 'bg-[var(--background-toggle-background-yes,#16241E)]'
              : 'bg-[var(--background-toggle-background-no,#E6DFCF)]',
            disabled &&
              checked &&
              'bg-[var(--background-toggle-background-yes-disabled,#666)] opacity-50',
            disabled &&
              !checked &&
              'bg-[var(--background-toggle-background-no-disabled,#E6DFCF)] opacity-50',
            'focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]',
            className
          )}
        >
          <div
            className={cn(
              'inline-block rounded-full shadow-[0px_1.667px_5px_0px_rgba(0,0,0,0.08)] transition-transform duration-200',
              'w-[20px] h-[20px]',
              checked
                ? 'bg-[var(--background-toggle-button-yes,white)] translate-x-[20px]'
                : 'bg-[var(--background-toggle-button-no,white)]',
              disabled &&
                checked &&
                'bg-[var(--background-toggle-button-yes-disabled,white)]',
              disabled &&
                !checked &&
                'bg-[var(--background-toggle-button-no-disabled,#F5EEDE)]'
            )}
          />
        </button>

        {hint && (
          <div className="flex gap-1 items-start">
            <div className="flex shrink-0 items-center mt-0.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="text-[var(--text-label-sub-label)]"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <text
                  x="8"
                  y="10"
                  textAnchor="middle"
                  fontSize="10"
                  fill="currentColor"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </div>
            <p id={hintId} className={hintClasses}>{hint}</p>
          </div>
        )}
      </div>
    );
  }
);

TeeToggle.displayName = 'TeeToggle';

export default TeeToggle;
